import api from '../../services/api';

export type FileSendLimits = {
  maxSizeBytes: number;
  maxExpireSeconds: number;
  defaultExpireSeconds: number;
  defaultMaxDownloads: number;
  expirePresetsSeconds: number[];
  downloadPresets: number[];
  maxChunkBytes: number;
};

export type CreateFileSendResponse = {
  id: string;
  uploadToken: string;
  ownerToken: string;
  expiresAt: string;
  maxDownloads: number;
  maxSizeBytes: number;
  maxChunkBytes: number;
};

export type FileSendShareListItem = {
  id: string;
  originalFilename: string | null;
  sizeBytes: number;
  maxDownloads: number;
  downloadCount: number;
  downloadsRemaining: number;
  expiresAt: string;
  createdAt: string;
  passwordRequired: boolean;
  encryptedSecret: string | null;
  expired: boolean;
  exhausted: boolean;
  active: boolean;
};

export async function fetchFileSendLimits(): Promise<FileSendLimits> {
  const { data } = await api.get<FileSendLimits>('/file-send/limits');
  return data;
}

export async function fetchMyFileSends(): Promise<FileSendShareListItem[]> {
  const { data } = await api.get<FileSendShareListItem[]>('/file-send');
  return data;
}

export async function createFileSend(body: {
  expireSeconds: number;
  maxDownloads: number;
  password?: string;
}): Promise<CreateFileSendResponse> {
  const { data } = await api.post<CreateFileSendResponse>('/file-send', body);
  return data;
}

export async function uploadFileSendChunk(
  id: string,
  uploadToken: string,
  index: number,
  chunk: Uint8Array,
): Promise<void> {
  const body = new Blob([chunk], { type: 'application/octet-stream' });
  await api.put(`/file-send/${id}/chunks/${index}`, body, {
    headers: {
      'Content-Type': 'application/octet-stream',
      'X-Upload-Token': uploadToken,
    },
    transformRequest: [(data) => data],
  });
}

export async function completeFileSend(
  id: string,
  uploadToken: string,
  body: {
    encryptedMetaBase64: string;
    authKeyBase64: string;
    sizeBytes: number;
    originalFilename?: string;
    encryptedSecret?: string;
  },
): Promise<void> {
  await api.post(`/file-send/${id}/complete`, body, {
    headers: { 'X-Upload-Token': uploadToken },
  });
}

export async function deleteFileSend(id: string, ownerToken?: string): Promise<void> {
  await api.delete(`/file-send/${id}`, ownerToken ? { data: { ownerToken } } : undefined);
}

export type ChallengeResponse = {
  nonce: string;
  passwordRequired: boolean;
  expiresAt: string;
  downloadsRemaining: number;
};

export async function fetchChallenge(id: string): Promise<ChallengeResponse> {
  const { data } = await api.get<ChallengeResponse>(`/file-send/${id}/challenge`, {
    skipGlobalErrorToast: true,
  } as never);
  return data;
}

export type AuthorizeResponse = {
  encryptedMetaBase64: string;
  downloadGrant: string;
  sizeBytes: number;
  expiresAt: string;
};

export async function authorizeDownload(
  id: string,
  signature: string,
  password?: string,
): Promise<AuthorizeResponse> {
  const { data } = await api.post<AuthorizeResponse>(
    `/file-send/${id}/authorize`,
    { signature, password },
    { skipGlobalErrorToast: true } as never,
  );
  return data;
}

export async function downloadBlob(id: string, grant: string): Promise<ArrayBuffer> {
  const { data } = await api.get<ArrayBuffer>(`/file-send/${id}/blob`, {
    headers: { 'X-Download-Grant': grant },
    responseType: 'arraybuffer',
    skipGlobalErrorToast: true,
  } as never);
  return data;
}

const OWNER_KEY = (id: string) => `fileSendOwner:${id}`;
const URL_STORE_KEY = 'ucomm.fileSend.urls';

export function storeOwnerToken(id: string, ownerToken: string): void {
  try {
    sessionStorage.setItem(OWNER_KEY(id), ownerToken);
  } catch {
    /* ignore */
  }
}

export function loadOwnerToken(id: string): string | null {
  try {
    return sessionStorage.getItem(OWNER_KEY(id));
  } catch {
    return null;
  }
}

function readUrlStore(): Record<string, string> {
  try {
    const raw = localStorage.getItem(URL_STORE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Record<string, string>;
  } catch {
    return {};
  }
}

export function storeShareUrl(id: string, url: string): void {
  try {
    const next = readUrlStore();
    next[id] = url;
    localStorage.setItem(URL_STORE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

export function loadShareUrl(id: string): string | null {
  const url = readUrlStore()[id];
  return typeof url === 'string' && url.length > 0 ? url : null;
}

export function forgetShareUrl(id: string): void {
  try {
    const next = readUrlStore();
    delete next[id];
    localStorage.setItem(URL_STORE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}
